import './TrnsxToken.css';

import { normalizeRecord } from 'views/TrnsxToken/TrnsxTokenModel';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';

// Utils
import User from 'utils/user.util';
import {
    GET_TRNSX_TOKEN_BY_EMAIL_OR_NAME,
    UPDATE_LAST_SENT,
    useLazyQuery,
    useMutation,
} from 'utils/graphql';
import { formatUTCDateTime } from 'utils/date.util';

const EMAIL_STATUS = {
    0: 'form',
    1: 'sending',
    2: 'error',
    3: 'success',
};

const TrnsxToken = () => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState([]);
    const [resultCount, setResultCount] = useState(0);
    const [tokenLimit] = useState(20);
    const [loading, setLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [debounceTimeout, setDebounceTimeout] = useState(null);
    const [sendEmailAction, setSendEmailAction] = useState(false);
    const [sendEmailStatus, setSendEmailStatus] = useState(0);
    const [recipientEmail, setRecipientEmail] = useState(null);
    const [recipientName, setRecipientName] = useState(null);
    const [recipientLanguage, setRecipientLanguage] = useState(null);

    const [getTokenByNameOrEmail] = useLazyQuery(GET_TRNSX_TOKEN_BY_EMAIL_OR_NAME, {
        fetchPolicy: 'cache-and-network',
    });

    const [updateLastSent] = useMutation(UPDATE_LAST_SENT);

    const encryptCode = (code) => {
        if (!code || code.length <= 3) return code || '';
        return '*'.repeat(code.length - 3) + code.slice(-3);
    };

    const handleSendEmail = async () => {
        // Check if recipientEmail and recipientName are both empty
        if (!recipientEmail || !recipientName) {
            return;
        }

        setSendEmailStatus(1);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_PROXY_URL || ''}/sendtemplateemail`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        authorization: `Bearer ${User.getToken()}`,
                    },
                    body: JSON.stringify({
                        from: {
                            email: 'onlinegiftcard@okzou.ca',
                            name: 'Retail Connection Portal',
                        },
                        emails: [recipientEmail || selectedRecord.customer_email],
                        template: 'eGiftCard',
                        data: {
                            FIRSTNAME: recipientName || selectedRecord.first_name,
                            GIFTCODE: selectedRecord.gift_code,
                            CARDVALUE: `$${Math.round(selectedRecord.CSL_CLOSING_BALANCE)}`,
                            STOREADDRESS: `${selectedRecord.mall} - ${selectedRecord.mall_address}`,
                            LANGUAGE: recipientLanguage,
                        },
                    }),
                }
            );
            if (response?.status !== 200) {
                setSendEmailStatus(2);
                return;
            }
            updateLastSent({
                variables: { id: selectedRecord.id, cwf_last_sent: formatUTCDateTime() },
            });
            setSendEmailStatus(3);
        } catch (e) {
            setSendEmailStatus(2);
        }
    };

    const makeSelection = (record) => {
        setRecipientName(record?.recipient);
        setRecipientEmail(record?.customer_email);
        setRecipientLanguage(record?.language == 'fr' ? 'FR' : 'EN');
        setSelectedRecord(normalizeRecord(record));
    };

    const searchTokens = async (search, { append = false, offset = 0 } = {}) => {
        const { data, error } = await getTokenByNameOrEmail({
            variables: {
                company_name: search,
                email: search,
                customer: search,
                name: search,
                recipient: search,
                order: search,
                limit: tokenLimit,
                offset,
            },
        });

        setLoading(false);

        if (error) {
            console.error('Error fetching tokens:', error);
            setResults([]);
            return;
        }
        const fetchedRecords = data?.table.records || [];
        setResultCount(data?.table.count || 0);

        if (append) {
            setResults((prev) => [...prev, ...fetchedRecords]);
        } else {
            setResults(fetchedRecords);
        }
    };

    useEffect(() => {
        // Clear results and selected record on new search
        setResults([]);
        setSelectedRecord(null);

        if (!searchQuery) return;

        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }

        // Set loading state here to show spinner at correct time
        setLoading(true);

        const timeout = setTimeout(() => {
            searchTokens(`${searchQuery}%`, { offset: 0 });
        }, 800);

        setDebounceTimeout(timeout);

        return () => clearTimeout(timeout);
    }, [searchQuery]);

    const handleLoadMore = () => {
        const nextOffset = results.length;
        setLoading(true);
        searchTokens(`${searchQuery}%`, { append: true, offset: nextOffset });
    };

    return (
        <div className="trnsx-token-container">
            <Form
                className="mb-4"
                onSubmit={(event) => {
                    event.preventDefault();
                }}
            >
                <Form.Group>
                    <Form.Label>{t('trnsx_token')}</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={t('search_by_name_email_recipient_order')}
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                </Form.Group>
            </Form>
            {loading && (
                <div className="d-flex justify-content-center mt-4">
                    <Spinner></Spinner>
                </div>
            )}
            {results.length === 0 && !loading && searchQuery && (
                <div className="alert alert-info mt-4">{t('no_results')}</div>
            )}
            {results.length > 0 &&
                !selectedRecord &&
                results.map((result) => {
                    const record = normalizeRecord(result.trnsx_ecom_activegc);
                    return (
                        <div
                            key={record.id}
                            className="result-item"
                            onClick={() => {
                                makeSelection(record);
                                setRecipientName(record.recipient);
                                setRecipientEmail(record.customer_email);
                                setRecipientLanguage(record.language == 'fr' ? 'FR' : 'EN');
                            }}
                            style={{
                                backgroundColor:
                                    record.type.toUpperCase() === 'B2B'
                                        ? 'var(--bg-color-blue)'
                                        : '',
                            }}
                        >
                            <div className="result-item-title">
                                <h5>
                                    {record.first_name} {record.last_name}
                                </h5>
                                {record.type.toUpperCase() === 'B2B' && (
                                    <div className="result-item-b2b-indicator">
                                        <p>{record.company_name}</p>
                                        <small>
                                            <strong> B2B</strong>
                                        </small>
                                    </div>
                                )}
                            </div>
                            <div className="result-item-details">
                                <p>{record.customer_email}</p>
                                <p>
                                    <strong>{t('gift_code')}:</strong>{' '}
                                    {encryptCode(record.gift_code)}
                                </p>
                            </div>
                        </div>
                    );
                })}
            {resultCount > results.length && !loading && !selectedRecord && (
                <div className="d-flex justify-content-center mt-4">
                    <button className="btn btn-secondary" onClick={handleLoadMore}>
                        {t('load_more')}
                    </button>
                </div>
            )}
            {selectedRecord && (
                <div
                    className="details-section mt-4"
                    style={{
                        backgroundColor:
                            selectedRecord.type.toUpperCase() === 'B2B'
                                ? 'var(--bg-color-blue)'
                                : '',
                    }}
                >
                    <h4>
                        {t('details')}
                        {selectedRecord.type.toUpperCase() === 'B2B' && (
                            <small>
                                <strong>B2B</strong>
                            </small>
                        )}
                    </h4>
                    {selectedRecord.type.toUpperCase() === 'B2B' && (
                        <p>
                            <strong>{t('company_name')}:</strong> {selectedRecord.company_name}
                        </p>
                    )}
                    <p>
                        <strong>ID:</strong> {selectedRecord.id}
                    </p>
                    <p>
                        <strong>{t('gift_code')}:</strong> {selectedRecord.gift_code}
                    </p>
                    <p>
                        <strong>{t('order_total')}:</strong> $
                        {Math.round(selectedRecord.CSL_CLOSING_BALANCE)}
                    </p>
                    <p>
                        <strong>{t('name')}:</strong> {selectedRecord.first_name}{' '}
                        {selectedRecord.last_name}
                        <br />
                        <strong>{t('email')}:</strong> {selectedRecord.customer_email}
                    </p>
                    <p>
                        <strong>{t('recipient')}:</strong> {selectedRecord.recipient}
                    </p>
                    <p>
                        <strong>{t('center')}:</strong> {selectedRecord.mall}
                        <br />
                        <strong>{t('address')}:</strong> {selectedRecord.mall_address}
                    </p>
                    <p>
                        <strong>{t('created')}:</strong>{' '}
                        {formatUTCDateTime(new Date(selectedRecord.created_at))}
                        <br />
                        <i>{selectedRecord.action_created_by}</i>
                    </p>
                    <div className="d-flex justify-content-between mt-4">
                        <button className="btn btn-secondary" onClick={() => makeSelection(null)}>
                            {t('close')}
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setSendEmailStatus(0);
                                setSendEmailAction(true);
                            }}
                        >
                            {t('resend_activation_email')}
                        </button>
                    </div>
                </div>
            )}
            <Modal
                show={sendEmailAction}
                onHide={() => setSendEmailAction(false)}
                centered
                size="lg"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('resend_activation_email')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {EMAIL_STATUS[sendEmailStatus] === 'form' && (
                        <Form>
                            <Form.Group>
                                <Form.Label>{t('recipient_name')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('name')}
                                    value={recipientName}
                                    onChange={(event) => setRecipientName(event.target.value)}
                                    required
                                />
                                <br />
                                <Form.Label>{t('recipient_email')}</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder={t('email')}
                                    value={recipientEmail}
                                    onChange={(event) => setRecipientEmail(event.target.value)}
                                    required
                                />
                                <br />
                                <Form.Label>{t('language')}</Form.Label>
                                <Form.Select
                                    onChange={(event) => setRecipientLanguage(event.target.value)}
                                    value={recipientLanguage}
                                >
                                    <option value="FR">Français</option>
                                    <option value="EN">English</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <div className="d-grid gap-2 mt-3">
                                    <Button
                                        variant="primary"
                                        onClick={() => handleSendEmail()}
                                        type="submit"
                                    >
                                        {t('send')}
                                    </Button>
                                </div>
                            </Form.Group>
                        </Form>
                    )}
                    {EMAIL_STATUS[sendEmailStatus] === 'sending' && (
                        <div className="d-flex flex-column align-items-center">
                            <p>{t('sending_email')}</p>
                            <Spinner animation="border" />
                        </div>
                    )}
                    {EMAIL_STATUS[sendEmailStatus] === 'error' && (
                        <div className="alert alert-danger">{t('error_sending_email')}</div>
                    )}
                    {EMAIL_STATUS[sendEmailStatus] === 'success' && (
                        <div className="alert alert-success">{t('email_sent_successfully')}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setSendEmailAction(false)}>
                        {t('close')}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TrnsxToken;
